import type { HubOrphan, HubQueueItem } from '@cotality/marketplace-contracts/endpoints/admin';

/** A pending Hub PR older than this is "stale" (advisory operator signal). */
export const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

export type HubQueueStatus = 'pending' | 'stale' | 'abandoned' | 'orphan';

interface HubQueuePendingRow {
  kind: 'pending';
  rowKey: string;
  /**
   * The plugin's id, carried separately from {@link rowKey} on purpose.
   * `rowKey` is a presentation concern — table identity — and the orphan arm
   * namespaces its own (`orphan:<name>`); giving this arm the symmetric
   * treatment (`pending:<id>`) would be a natural consistency edit that
   * silently broke every force-auto-complete POST, since the route's param is
   * `z.string().uuid()`. Read this at call sites that need an id.
   */
  pluginId: string;
  name: string;
  team: string | null;
  timestamp: string;
  hubPrUrl: string;
  status: 'pending' | 'stale' | 'abandoned';
}

interface HubQueueOrphanRow {
  kind: 'orphan';
  rowKey: string;
  name: string;
  source: string;
  timestamp: string;
  status: 'orphan';
}

/**
 * Discriminated on `kind` so a pending-PR row can't carry an orphan's
 * `source`, and an orphan row can't carry a `team`/`hubPrUrl` — the two row
 * shapes are genuinely disjoint (see `pendingToRow`/`orphanToRow`), and this
 * makes the disjointness a compile-time fact instead of a mapper-function
 * convention.
 */
export type HubQueueRow = HubQueuePendingRow | HubQueueOrphanRow;

/** `!= null` (not `!==`): a stale replica/cache may omit the field. */
export function isStale(item: HubQueueItem, now: number): boolean {
  if (item.abandonedAt != null) return false;
  return now - new Date(item.createdAt).getTime() > STALE_THRESHOLD_MS;
}

function pendingToRow(item: HubQueueItem, now: number): HubQueuePendingRow {
  const status: HubQueuePendingRow['status'] =
    item.abandonedAt != null ? 'abandoned' : isStale(item, now) ? 'stale' : 'pending';
  return {
    kind: 'pending',
    rowKey: item.id,
    pluginId: item.id,
    name: item.name,
    team: item.teamDisplayName,
    timestamp: item.createdAt,
    hubPrUrl: item.hubPrUrl,
    status,
  };
}

function orphanToRow(o: HubOrphan): HubQueueOrphanRow {
  return {
    kind: 'orphan',
    rowKey: `orphan:${o.name}`,
    name: o.name,
    source: o.source,
    timestamp: o.firstSeenAt,
    status: 'orphan',
  };
}

/** Merge both API result sets into one table model (pending rows first). */
export function toHubQueueRows(
  items: readonly HubQueueItem[],
  orphans: readonly HubOrphan[],
  now: number,
): HubQueueRow[] {
  return [...items.map((i) => pendingToRow(i, now)), ...orphans.map(orphanToRow)];
}

/**
 * HubQueue tab config — column definitions + tab metadata. Powers
 * `<HubQueueTab/>`. Gate: `admin:view`.
 *
 * Surfaces plugins whose sync-repo Hub PR is open but not yet merged
 * (`hub_pr_url IS NOT NULL AND hub_listed_at IS NULL`). Operators use
 * this to track Hub PRs awaiting marketplace-admin review against
 * `Cotality.Marketplace.Hub`. Per ADR-0003 + Task 12 of the Hub
 * bootstrap plan.
 */
export const hubQueueTabConfig = {
  id: 'hub-queue',
  label: 'Hub Queue',
  permissions: ['admin:view'],
  emptyState: 'No pending Hub PRs and no orphaned listings.',
} as const;

/** Screen-reader label shared by the sidebar and admin-tab count badges. */
export function hubQueueBadgeLabel(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'} pending in hub queue`;
}
